if (!customElements.get('media-gallery')) {
    customElements.define(
        'media-gallery',
        class MediaGallery extends HTMLElement {
            constructor() {
                super();
                this.elements = {
                    liveRegion: this.querySelector('[id^="GalleryStatus"]'),
                    viewer: this.querySelector('[id^="GalleryViewer"]'),
                    thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
                    next: this.querySelector('.a-carousel-button[aria-label="next slide"]'),
                    previous: this.querySelector('.a-carousel-button[aria-label="previous slide"]'),
                    stageLock: this.querySelector('.a-media-stage-lock'),
                    photosStage: this.querySelector('.a-media-photos-stage'),
                    videoStage: this.querySelector('.a-media-video-stage'),
                    galleryTabs: this.querySelector('.a-gallery-tabs'),
                    photosTab: this.querySelector('[data-gallery-tab="photos"]'),
                    videoTab: this.querySelector('[data-gallery-tab="video"]'),
                    videoModalOpener: this.querySelector('.a-video-modal-opener'),
                };
                this.mql = window.matchMedia('(min-width: 750px)');
                this.hasVideo = this.dataset.hasVideo === 'true' && !!this.elements.videoStage;
                this.mode = 'photos';
                this.lockedHeight = 0;

                this.boundResize = this.onWindowResize.bind(this);
                this.boundModalClosed = this.handleModalClosed.bind(this);
                window.addEventListener('resize', this.boundResize);
                document.body.addEventListener('modalClosed', this.boundModalClosed);

                this.bindTabs();
                this.bindSliderInteractions();
                this.syncStageHeight();
                window.requestAnimationFrame(() => this.syncStageHeight());
                if (this.elements.photosStage) {
                    this.elements.photosStage.querySelectorAll('img').forEach((image) => {
                        image.addEventListener('load', () => this.syncStageHeight(), { once: true });
                    });
                }

                if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
            }

            disconnectedCallback() {
                window.removeEventListener('resize', this.boundResize);
                document.body.removeEventListener('modalClosed', this.boundModalClosed);
            }

            bindTabs() {
                if (!this.hasVideo || !this.elements.photosTab || !this.elements.videoTab) return;

                this.elements.photosTab.addEventListener('click', () => this.setViewMode('photos'));
                this.elements.videoTab.addEventListener('click', () => {
                    if (this.mql.matches) {
                        this.setViewMode('video');
                        return;
                    }
                    this.openMobileVideoModal();
                });
            }

            bindSliderInteractions() {
                if (!this.elements.thumbnails || !this.elements.viewer) return;

                this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
                this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
                    mediaToSwitch
                        .querySelector('button')
                        .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
                });

                if (this.elements.next) {
                    this.elements.next.addEventListener('click', () => {
                        const children = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
                        const length = children.length;
                        const current = children.find((elem) => elem.querySelector('[aria-current]'));
                        if (!current || !length) return;

                        const position = Number(current.getAttribute('data-media-position'));
                        const newCurrent = position >= length ? 1 : position + 1;
                        const nextTarget = this.elements.thumbnails.querySelector(
                            `[data-target][data-media-position='${newCurrent}']`
                        );
                        if (nextTarget) this.setActiveMedia(nextTarget.dataset.target, false);
                    });
                }

                if (this.elements.previous) {
                    this.elements.previous.addEventListener('click', () => {
                        const children = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'));
                        const length = children.length;
                        const current = children.find((elem) => elem.querySelector('[aria-current]'));
                        if (!current || !length) return;

                        const position = Number(current.getAttribute('data-media-position'));
                        const newCurrent = position === 1 ? length : position - 1;
                        const previousTarget = this.elements.thumbnails.querySelector(
                            `[data-target][data-media-position='${newCurrent}']`
                        );
                        if (previousTarget) this.setActiveMedia(previousTarget.dataset.target, false);
                    });
                }
            }

            onSlideChanged(event) {
                const thumbnail = this.elements.thumbnails.querySelector(
                    `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
                );
                this.setActiveThumbnail(thumbnail);
            }

            setViewMode(mode) {
                if (!this.hasVideo || !this.elements.stageLock || !this.elements.photosStage || !this.elements.videoStage) return;

                if (mode === 'video') {
                    window.pauseAllMedia();
                    this.syncStageHeight(true);
                    this.mode = 'video';
                    this.elements.photosStage.setAttribute('hidden', '');
                    this.elements.videoStage.removeAttribute('hidden');
                    this.elements.stageLock.classList.add('a-mode-video');
                    if (this.lockedHeight > 0) {
                        this.elements.stageLock.style.height = `${this.lockedHeight}px`;
                        this.elements.stageLock.style.minHeight = `${this.lockedHeight}px`;
                    }
                    this.updateTabState();
                    this.playDesktopVideo();
                    return;
                }

                this.mode = 'photos';
                this.elements.videoStage.setAttribute('hidden', '');
                this.elements.photosStage.removeAttribute('hidden');
                this.elements.stageLock.classList.remove('a-mode-video');
                this.updateTabState();

                window.requestAnimationFrame(() => {
                    this.syncStageHeight(true);
                });
            }

            playDesktopVideo() {
                const video = this.elements.videoStage?.querySelector('video');
                if (!video) return;

                const playPromise = video.play?.();
                if (playPromise?.catch) playPromise.catch(() => {});
            }

            updateTabState() {
                if (!this.elements.photosTab || !this.elements.videoTab) return;

                const isVideo = this.mode === 'video';
                this.elements.photosTab.classList.toggle('a-gallery-tab--active', !isVideo);
                this.elements.videoTab.classList.toggle('a-gallery-tab--active', isVideo);
                this.elements.photosTab.setAttribute('aria-selected', (!isVideo).toString());
                this.elements.videoTab.setAttribute('aria-selected', isVideo.toString());
                if (this.elements.galleryTabs) {
                    this.elements.galleryTabs.setAttribute('data-active-tab', isVideo ? 'video' : 'photos');
                }
            }

            syncStageHeight(force = false) {
                if (!this.hasVideo || !this.elements.photosStage || !this.elements.stageLock) return;
                if (!this.mql.matches) {
                    this.elements.stageLock.style.height = '';
                    this.elements.stageLock.style.minHeight = '';
                    return;
                }

                if (this.mode !== 'photos' && !force) return;

                const nextHeight = this.elements.photosStage.offsetHeight;
                if (!nextHeight) return;

                this.lockedHeight = nextHeight;
                this.elements.stageLock.style.minHeight = `${nextHeight}px`;
                this.elements.stageLock.style.height = `${nextHeight}px`;
            }

            onWindowResize() {
                if (!this.hasVideo) return;

                if (!this.mql.matches) {
                    this.mode = 'photos';
                    if (this.elements.videoStage) this.elements.videoStage.setAttribute('hidden', '');
                    if (this.elements.photosStage) this.elements.photosStage.removeAttribute('hidden');
                    if (this.elements.stageLock) {
                        this.elements.stageLock.classList.remove('a-mode-video');
                        this.elements.stageLock.style.height = '';
                        this.elements.stageLock.style.minHeight = '';
                    }
                    this.updateTabState();
                    return;
                }

                this.syncStageHeight(true);
            }

            openMobileVideoModal() {
                const opener = this.elements.videoModalOpener;
                if (!opener) return;

                const modalSelector = opener.getAttribute('data-modal');
                const modal = modalSelector ? document.querySelector(modalSelector) : null;
                if (!modal || typeof modal.show !== 'function') return;

                modal.classList.add('a-video-modal-open');
                modal.show(opener);
            }

            handleModalClosed() {
                document.querySelectorAll('.a-video-modal-open').forEach((modal) => {
                    modal.classList.remove('a-video-modal-open');
                });
            }

            setActiveMedia(mediaId, prepend) {
                if (!this.elements.viewer) return;

                const activeMedia =
                    this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
                    this.elements.viewer.querySelector('[data-media-id]');
                if (!activeMedia) {
                    return;
                }
                this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
                    element.classList.remove('is-active');
                });
                activeMedia?.classList?.add('is-active');

                if (prepend) {
                    activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

                    if (this.elements.thumbnails) {
                        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
                        if (activeThumbnail) {
                            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                                activeThumbnail.parentElement.prepend(activeThumbnail);
                        }
                    }

                    if (this.elements.viewer.slider) this.elements.viewer.resetPages();
                }

                this.preventStickyHeader();
                window.setTimeout(() => {
                    if (!this.mql.matches || this.elements.thumbnails) {
                        activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
                    }
                });
                this.playActiveMedia(activeMedia);

                if (!this.elements.thumbnails) return;
                const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
                this.setActiveThumbnail(activeThumbnail);
                if (activeThumbnail) this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
            }

            setActiveThumbnail(thumbnail) {
                if (!this.elements.thumbnails || !thumbnail) return;

                this.elements.thumbnails
                    .querySelectorAll('button')
                    .forEach((element) => element.removeAttribute('aria-current'));
                thumbnail.querySelector('button').setAttribute('aria-current', true);
                if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

                this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
            }

            announceLiveRegion(activeItem, position) {
                const image = activeItem.querySelector('.product__modal-opener--image img');
                if (!image) return;
                image.onload = () => {
                    this.elements.liveRegion.setAttribute('aria-hidden', false);
                    this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
                    setTimeout(() => {
                        this.elements.liveRegion.setAttribute('aria-hidden', true);
                    }, 2000);
                };
                image.src = image.src;
            }

            playActiveMedia(activeItem) {
                window.pauseAllMedia();
                const deferredMedia = activeItem.querySelector('.deferred-media');
                if (deferredMedia) deferredMedia.loadContent(false);
            }

            preventStickyHeader() {
                this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
                if (!this.stickyHeader) return;
                this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
            }

            removeListSemantic() {
                if (!this.elements.viewer || !this.elements.viewer.slider) return;
                this.elements.viewer.slider.setAttribute('role', 'presentation');
                this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
            }
        }
    );
}
